import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import axios from "axios";
import { ShieldCheck, CheckCircle, WarningCircle, XCircle, Hash, Calendar, Certificate, Printer, ArrowSquareOut } from "@phosphor-icons/react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { motion } from "framer-motion";

export default function PublicCertificate() {
  const { token } = useParams();
  const [cert, setCert] = useState(null);
  const [state, setState] = useState({ loading: true, error: "" });

  useEffect(() => {
    (async () => {
      try {
        // Public endpoint — do not send credentials
        const r = await axios.get(`${API}/public/certificate/${token}`);
        setCert(r.data);
        setState({ loading: false, error: "" });
      } catch (e) {
        let msg = "Unable to load certificate";
        if (e?.response?.status === 410) msg = e.response.data?.detail || "Certificate is no longer valid";
        else if (e?.response?.status === 404) msg = "Certificate not found";
        setState({ loading: false, error: msg });
      }
    })();
  }, [token]);

  if (state.loading) return (
    <div className="min-h-screen aurora flex items-center justify-center">
      <div className="glass rounded-2xl px-10 py-8 text-center">
        <div className="eyebrow mb-3">Verifying certificate…</div>
        <span className="dot-flash"/><span className="dot-flash"/><span className="dot-flash"/>
      </div>
    </div>
  );

  if (state.error) return (
    <div className="min-h-screen aurora flex items-center justify-center px-6">
      <div className="glass rounded-2xl px-10 py-10 text-center max-w-md">
        <XCircle size={40} weight="duotone" color="#E63946" className="mx-auto mb-4"/>
        <div className="font-display text-2xl mb-2">Certificate not valid</div>
        <div className="text-white/60 text-sm">{state.error}</div>
        <Link to="/" className="btn-ghost mt-6 inline-flex">← Back to PerCen360</Link>
      </div>
    </div>
  );

  const p = cert.payload;
  let scoreColor = "#E63946";
  if (p.compliance_score >= 90) scoreColor = "#00A86B";
  else if (p.compliance_score >= 70) scoreColor = "#FFB020";
  const highScore = p.compliance_score >= 90;
  const ringBg = highScore ? "rgba(0,168,107,0.15)" : "rgba(255,176,32,0.15)";
  const ringBorder = highScore ? "#00A86B" : "#FFB020";
  const scope = p.scope;
  const pieData = [
    { name: "Pass", value: p.receipts_pass, color: "#00A86B" },
    { name: "Warn", value: p.receipts_warn, color: "#FFB020" },
    { name: "Fail", value: p.receipts_fail, color: "#E63946" },
  ].filter(x => x.value > 0);

  return (
    <div className="min-h-screen aurora grain print:aurora-print">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .aurora { background: white !important; color: black !important; }
          .glass { background: white !important; border: 1px solid #ddd !important; }
          .text-white\\/60, .text-white\\/70, .text-white\\/80, .text-white\\/90 { color: #333 !important; }
        }
      `}</style>

      {/* Top bar (screen only) */}
      <header className="hairline-b no-print">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center gold-glow" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display text-lg leading-none">PerCen360</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">Verified Certificate</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="btn-ghost text-sm" data-testid="btn-print-cert"><Printer size={14}/> Print / PDF</button>
            <Link to="/" className="btn-gold text-sm">Try PerCen360 <ArrowSquareOut size={14}/></Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 sm:px-10 py-12">
        {/* Hero card */}
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}}
          data-testid="certificate-hero"
          className="glass rounded-3xl p-10 sm:p-14 relative overflow-hidden">
          {/* Watermark */}
          <div className="absolute -right-16 -top-16 opacity-[0.06] pointer-events-none">
            <Certificate size={280} weight="duotone" color="#FFB020" />
          </div>

          <div className="relative">
            <div className="eyebrow mb-4 flex items-center gap-3">
              <span className="w-8 h-px" style={{ background: "#FFB020" }}/>
              Compliance Certificate · Issued by PerCen360
            </div>

            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight">{p.org_name}</h1>
                <div className="text-white/60 mt-2 text-sm">
                  {scope.type === "single_grant" ? (
                    <>Certificate scope: <span className="text-white/90">{scope.grant_name}</span> — {scope.programme}</>
                  ) : (
                    <>Certificate scope: <span className="text-white/90">Organisation-wide</span> — {scope.grants_count} grant(s)</>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Compliance Score</div>
                  <div className="font-display text-5xl leading-none mt-2 mono" style={{ color: scoreColor }}>
                    {p.compliance_score}%
                  </div>
                </div>
                <div className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: ringBg, border: `2px solid ${ringBorder}` }}>
                  {p.compliance_score >= 90
                    ? <CheckCircle size={30} weight="fill" color="#00A86B"/>
                    : <WarningCircle size={30} weight="fill" color="#FFB020"/>}
                </div>
              </div>
            </div>

            {/* Meta strip */}
            <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              <MetaItem icon={Calendar} label="Issued" value={cert.issued_at?.slice(0,10)} />
              <MetaItem icon={Calendar} label="Valid until" value={cert.expires_at?.slice(0,10)} />
              <MetaItem icon={Hash} label="Audit hash" value={cert.audit_hash} mono />
              <MetaItem icon={ShieldCheck} label="Certificate ID" value={cert.cert_id} mono />
            </div>
          </div>
        </motion.div>

        {/* Financials + Chart */}
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          <div className="glass rounded-2xl p-6 md:col-span-1">
            <div className="eyebrow mb-4">Receipt integrity</div>
            {(p.receipts_pass + p.receipts_warn + p.receipts_fail) === 0 ? (
              <div className="text-white/50 text-sm py-8 text-center">No receipts filed yet.</div>
            ) : (
              <>
                <div className="h-40">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={pieData} innerRadius={40} outerRadius={65} paddingAngle={4} dataKey="value">
                        {pieData.map((e) => <Cell key={e.name} fill={e.color} stroke="none" />)}
                      </Pie>
                      <Tooltip contentStyle={{ background:"#0A1311", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10, color:"#F3F5F4" }}/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <StatCell label="Pass" value={p.receipts_pass} color="#00A86B"/>
                  <StatCell label="Warn" value={p.receipts_warn} color="#FFB020"/>
                  <StatCell label="Fail" value={p.receipts_fail} color="#E63946"/>
                </div>
                <div className="text-center text-white/60 text-xs mt-3">{p.receipts_total} receipts audited</div>
              </>
            )}
          </div>

          <div className="glass rounded-2xl p-6 md:col-span-2">
            <div className="eyebrow mb-4">Grant utilisation</div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <BigFigure label="Awarded" value={`€${Number(scope.total_awarded||0).toLocaleString()}`} />
              <BigFigure label="Spent" value={`€${Number(scope.total_spent||0).toLocaleString()}`} color="#FFB020"/>
              <BigFigure label="Remaining" value={`€${Number(scope.remaining||0).toLocaleString()}`} color="#00A86B"/>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, scope.utilization_pct || 0)}%`, background: "linear-gradient(90deg,#00A86B,#FFB020)" }}/>
            </div>
            <div className="text-xs text-white/50 mt-2">{scope.utilization_pct}% of awarded funding utilised</div>
          </div>
        </div>

        {/* Category breakdown */}
        {(scope.by_category?.length > 0) && (
          <div className="glass rounded-2xl p-6 mt-5">
            <div className="eyebrow mb-4">Category breakdown</div>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={scope.by_category}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="category" tick={{ fill: "#9BA3A0", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#9BA3A0", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background:"#0A1311", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10 }}/>
                  <Bar dataKey="amount" fill="#FFB020" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Verification block */}
        <div className="glass rounded-2xl p-8 mt-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <div>
              <div className="eyebrow mb-2">How to verify this certificate</div>
              <p className="text-white/70 text-sm max-w-2xl leading-relaxed">
                This certificate was generated by PerCen360 from the holder&apos;s live compliance data.
                The <span className="mono text-white/90">audit hash</span> is derived from certificate ID, compliance score, spend total, receipt count, and issue time.
                It is invalid if revoked or expired. Contact <a href="mailto:gillian@percen360.com" className="text-[#FFB020] underline">gillian@percen360.com</a> to independently verify.
              </p>
            </div>
            <div className="shrink-0 rounded-xl border border-white/10 bg-black/30 p-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-white/45">Viewed</div>
              <div className="mono text-2xl mt-1">{cert.view_count}×</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-xs text-white/45">
          <div>© {new Date().getFullYear()} PerCen Technologies Ltd · percen360.com · EU region · GDPR compliant</div>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white/80">Privacy</Link>
            <Link to="/terms" className="hover:text-white/80">Terms</Link>
            <Link to="/dpia" className="hover:text-white/80">DPIA</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

const MetaItem = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
      <Icon size={14} weight="duotone" color="#FFB020"/>
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-white/45">{label}</div>
      <div className={`text-sm mt-1 truncate ${mono ? "mono" : ""}`}>{value}</div>
    </div>
  </div>
);

const StatCell = ({ label, value, color }) => (
  <div className="rounded-lg bg-black/25 border border-white/5 p-2">
    <div className="text-[9px] uppercase text-white/45 tracking-wider">{label}</div>
    <div className="mono text-sm mt-1" style={{ color }}>{value}</div>
  </div>
);

const BigFigure = ({ label, value, color }) => (
  <div className="rounded-xl bg-black/25 border border-white/5 p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/45">{label}</div>
    <div className="mono text-xl mt-2" style={{ color: color || "#F3F5F4" }}>{value}</div>
  </div>
);
